import React, {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"

import { Button } from "@coval/ui/button"
import { useWavesurfer } from "@wavesurfer/react"
import { type RegionParams } from "wavesurfer.js/dist/plugins/regions.js"

import { logger } from "@/lib/logger"
import { useTranscriptParticipants } from "@/components/transcript/TranscriptParticipantsContext"
import {
  ESCAPE_SHORTCUT_PRIORITY,
  useEscapeShortcut,
} from "@/hooks/useEscapeShortcut"
import type { WavesurferPeaks } from "@/hooks/usePrecomputedPeaks"
import type { RunRow } from "@/types/RunRow"
import { downloadBlob } from "@/utils/download"

import { AudioMarkerDialog } from "./AudioMarkerDialog"
import {
  CompactAudioControls,
  CompactAudioTime,
  CONTROLS_EDGE_PIXELS,
  controlsInsetPixels,
  FullAudioControls,
} from "./AudioPlayerControls"
import {
  AddAudioMarkerButton,
  AudioMarkerFlags,
  AudioRegionSelectionMenu,
  AudioTrackTail,
  AudioWaveformContainer,
} from "./AudioPlayerOverlays"
import {
  serializeAudioRegions,
  type AudioRegionParams,
} from "./audioRegionController"
import { AudioSeekSlider } from "./AudioSeekSlider"
import {
  SPEAKER_GUTTER_PIXELS,
  SpeakerChannelLayers,
  SpeakerLaneAvatars,
  SpeakerLaneBands,
} from "./AudioSpeakerLanes"
import { useAudioMarkerController } from "./useAudioMarkerController"
import { useAudioRegionSync } from "./useAudioRegionSync"
import { useAudioWaveformLifecycle } from "./useAudioWaveformLifecycle"
import { useAudioWaveformOptions } from "./useAudioWaveformOptions"

const log = logger("AudioPlayer")
export type { AudioRegionParams } from "./audioRegionController"
export interface AudioPlayerHandle {
  /** Returns the decoded AudioBuffer from the loaded audio, or null if not ready. */
  getDecodedData: () => AudioBuffer | null
  getDuration: () => number
  seekTo: (seconds: number) => void
}
interface AudioPlayerProps {
  showPlaybackCursor?: boolean
  showHoverPreview?: boolean
  waveformVisible?: boolean
  controlsExtra?: React.ReactNode
  /** Sits on the controls row's left margin. */
  controlsLeading?: React.ReactNode
  channelContent?: { height: number; lanes: React.ReactNode[] }
  channelWaveformOverlay?: React.ReactNode[]
  waveformOverlay?: React.ReactNode
  seekRequestId?: number
  time?: number
  compact?: boolean
  signedUrl?: string
  /** Stable recording identity; refreshed signed URLs still refer to the same media. */
  recordingKey?: string
  isErrorAudio?: boolean
  regions?: RegionParams[]
  /** When provided, regions are editable: drag/resize, click to delete, double-click empty to add */
  onRegionsChange?: (regions: AudioRegionParams[]) => void
  /** When true, editable regions keep full start/end durations instead of collapsing to markers */
  useRegionsForEditing?: boolean
  onRegionPlayed?: (region: AudioRegionParams) => void
  onIsPlayingChange?: (isPlaying: boolean) => void
  /** Called as playback position advances (seconds). Caller should throttle if needed. */
  onTimeUpdate?: (currentTimeSeconds: number) => void
  /** Reports the applied position and original target so consumers can identify clamped seek acknowledgements. */
  onSeek?: (time: number, seekTarget?: number) => void
  /** Called when the reader clicks or drags on the waveform itself (seconds), never for a programmatic seek. */
  onWaveformInteraction?: (time: number) => void
  onReady?: () => void
  /** Called when the media fails to load or decode, and with null once it loads. */
  onLoadError?: (error: Error | null) => void
  togglePlayPauseRequestId?: number
  simulationOutputId?: string
  humanEvalMode?: boolean
  currentSelection?: RunRow
  /** When provided, called instead of default add-region on waveform double-click. */
  onWaveformDoubleClick?: (
    time: number,
    position: { x: number; y: number },
  ) => void
  /** Extra content rendered inside the selected-region popup menu. */
  regionMenuExtra?: (regionId: string) => React.ReactNode
  /** When set, seek to start, play the range, and pause at end. Change requestId to re-trigger. */
  playRange?: { start: number; end: number; requestId: number }
  /** Precomputed waveform peaks for instant rendering before audio decodes. */
  precomputedPeaks?: WavesurferPeaks | null
  precomputedPeaksPending?: boolean
  /** Audio duration in seconds — enables time display before audio decodes when peaks are provided. */
  audioDuration?: number | null
  /** Pixel insets from the player edges to the waveform well; avatars occupy the left gutter and tailPx extends the timeline past the recording end. */
  trackInsetPx?: { leftPx: number; rightPx: number; tailPx?: number } | null
  /** Says what is missing about the recording's transcript; the controls show it and are described by it. */
  transcriptNotice?: string | null
}

export const AudioPlayer = React.forwardRef<
  AudioPlayerHandle,
  AudioPlayerProps
>(
  (
    {
      controlsExtra,
      controlsLeading,
      channelContent,
      channelWaveformOverlay,
      waveformOverlay,
      waveformVisible = true,
      showHoverPreview = true,
      showPlaybackCursor = true,
      time,
      seekRequestId,
      compact = false,
      signedUrl,
      recordingKey,
      isErrorAudio,
      onRegionPlayed,
      onRegionsChange,
      useRegionsForEditing = false,
      regions,
      onIsPlayingChange,
      onTimeUpdate,
      onSeek,
      onWaveformInteraction,
      onReady,
      onLoadError,
      togglePlayPauseRequestId,
      simulationOutputId,
      currentSelection,
      humanEvalMode = false,
      onWaveformDoubleClick,
      regionMenuExtra,
      playRange,
      precomputedPeaks,
      precomputedPeaksPending,
      audioDuration,
      trackInsetPx = null,
      transcriptNotice = null,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    const [playbackRate, setPlaybackRate] = useState(1)
    const [isDownloading, setIsDownloading] = useState(false)
    const baseWaveformOptions = useAudioWaveformOptions(compact)
    const contentHeight = !compact ? (channelContent?.height ?? 0) : 0
    const {
      cursorColor,
      height: waveformHeight,
      laneHeight,
      progressColor,
      splitChannels,
      waveColor,
    } = useAudioWaveformOptions(compact, contentHeight)
    const transcriptParticipants = useTranscriptParticipants()
    const noticeId = useId()
    // Only the full controls row shows the notice this id names.
    const notice =
      transcriptNotice && !compact
        ? { id: noticeId, text: transcriptNotice }
        : undefined
    const loadablePrecomputedPeaks = useMemo(() => {
      if (!precomputedPeaks || !splitChannels) {
        return precomputedPeaks
      }
      // A legacy mono mix cannot supply both recorded speaker lanes.
      return precomputedPeaks.length >= splitChannels.length
        ? precomputedPeaks
        : null
    }, [precomputedPeaks, splitChannels])
    const { wavesurfer, isPlaying, isReady, currentTime } = useWavesurfer({
      container: containerRef as React.RefObject<HTMLElement>,
      waveColor,
      progressColor,
      normalize: true,
      minPxPerSec: 0,
      height: baseWaveformOptions.height,
      cursorWidth: showPlaybackCursor ? 2 : 0,
      cursorColor,
      dragToSeek: true,
      splitChannels: baseWaveformOptions.splitChannels,
    })

    useEffect(() => {
      wavesurfer?.setOptions({ height: waveformHeight, splitChannels })
    }, [wavesurfer, waveformHeight, splitChannels])

    useEffect(() => {
      if (!wavesurfer || !onWaveformInteraction) {
        return
      }
      return wavesurfer.on("interaction", onWaveformInteraction)
    }, [wavesurfer, onWaveformInteraction])

    const hasSpeakerLanes =
      !compact &&
      isReady &&
      (wavesurfer?.getDecodedData()?.numberOfChannels ?? 0) >= 2
    const speakerAvatarParticipants = hasSpeakerLanes
      ? transcriptParticipants
      : undefined

    const {
      regionSelection,
      regionsPluginRef,
      styleResizeHandles,
      updateRegionBounds,
    } = useAudioRegionSync({
      compact,
      channelWaveformHeight: contentHeight > 0 ? laneHeight : undefined,
      isReady,
      onRegionPlayed,
      onRegionsChange,
      regions,
      useRegionsForEditing,
      wavesurfer,
    })

    const {
      addMarker,
      buttonPosition,
      editingMarker,
      handleAddMarkerAtPosition,
      handleButtonMouseEnter,
      handleButtonMouseLeave,
      handleEditMarker,
      handleModalClose,
      handleUpdateMarker,
      handleWaveformMouseLeave,
      handleWaveformMouseMove,
      hoverTime,
      isButtonHovered,
      isHovering,
      manualTime,
      markers,
      newMarkerLabel,
      setManualTime,
      setNewMarkerLabel,
      showAddMarkerModal,
    } = useAudioMarkerController({
      anchorRef: rootRef,
      containerRef,
      wavesurfer,
    })
    const { loadError, onPlayPause, seekTo } = useAudioWaveformLifecycle({
      waveformVisible: waveformVisible && showHoverPreview,
      audioDuration,
      currentTime,
      isPlaying,
      isReady,
      loadablePrecomputedPeaks,
      precomputedPeaksPending,
      onIsPlayingChange,
      onReady,
      onTimeUpdate,
      onSeek,
      playRange,
      playbackRate,
      recordingKey:
        recordingKey ??
        (currentSelection
          ? [
              currentSelection.simulation_output_id,
              currentSelection.simulation_s3_bucket,
              currentSelection.simulation_s3_key,
            ].join("::")
          : undefined),
      regions,
      signedUrl,
      time,
      seekRequestId,
      togglePlayPauseRequestId,
      wavesurfer,
    })

    useImperativeHandle(
      ref,
      () => ({
        getDecodedData: () => wavesurfer?.getDecodedData() ?? null,
        getDuration: () => wavesurfer?.getDuration() ?? 0,
        seekTo,
      }),
      [wavesurfer, seekTo],
    )

    const handlePlaybackRateChange = useCallback(
      (newRate: number) => {
        setPlaybackRate(newRate)
        wavesurfer?.setPlaybackRate(newRate)
      },
      [wavesurfer],
    )

    const handleDownload = useCallback(async () => {
      if (!signedUrl) {
        return
      }

      try {
        setIsDownloading(true)

        const response = await fetch(signedUrl)
        if (!response.ok) {
          throw new Error("Failed to fetch audio file")
        }

        const blob = await response.blob()

        let extension = "wav" // default
        if (blob.type) {
          if (blob.type.includes("wav")) {
            extension = "wav"
          } else if (blob.type.includes("m4a") || blob.type.includes("mp4")) {
            extension = "m4a"
          } else if (blob.type.includes("ogg")) {
            extension = "ogg"
          } else if (blob.type.includes("webm")) {
            extension = "webm"
          }
        }

        const filename = simulationOutputId
          ? `${simulationOutputId}.${extension}`
          : `audio.${extension}`

        downloadBlob(blob, filename)
      } catch (error) {
        log.error("Error downloading audio", error)
      } finally {
        setIsDownloading(false)
      }
    }, [signedUrl, simulationOutputId])

    useEscapeShortcut({
      allowInInputs: true,
      description: "Close add marker dialog",
      enabled: showAddMarkerModal,
      handler: handleModalClose,
      priority: ESCAPE_SHORTCUT_PRIORITY.DIALOG,
    })

    const handleWaveformDoubleClick = useCallback(
      (e: React.MouseEvent) => {
        if (!containerRef.current || !wavesurfer || !isReady) {
          return
        }
        const plugin = regionsPluginRef.current
        if (plugin && onRegionsChange) {
          const isOnRegion = plugin
            .getRegions()
            .some((r) => r.element?.contains(e.target as Node))
          if (isOnRegion) {
            return
          }
        }
        const rect = containerRef.current.getBoundingClientRect()
        const x = e.clientX - rect.left
        const duration = wavesurfer.getDuration()
        const time = Math.max(
          0,
          Math.min(duration, (x / rect.width) * duration),
        )

        if (onWaveformDoubleClick) {
          onWaveformDoubleClick(time, { x: e.clientX, y: e.clientY })
          return
        }

        if (!plugin || !onRegionsChange) {
          return
        }

        const end = useRegionsForEditing ? Math.min(duration, time + 1) : time
        plugin.addRegion({
          start: time,
          end,
          color: "rgba(147, 51, 234, 0.5)",
          content: useRegionsForEditing
            ? `${(end - time).toFixed(2)}s`
            : undefined,
          drag: true,
          resize: useRegionsForEditing,
        })
        onRegionsChange(serializeAudioRegions(plugin.getRegions()))
        if (useRegionsForEditing) {
          requestAnimationFrame(() => requestAnimationFrame(styleResizeHandles))
        }
      },
      [
        onRegionsChange,
        onWaveformDoubleClick,
        regionsPluginRef,
        wavesurfer,
        isReady,
        useRegionsForEditing,
        styleResizeHandles,
      ],
    )

    const handleAddSegmentAtCurrentTime = useCallback(() => {
      if (!wavesurfer || !isReady) {
        return
      }

      const duration = wavesurfer.getDuration()
      const time = wavesurfer.getCurrentTime()

      if (onWaveformDoubleClick) {
        onWaveformDoubleClick(time, { x: 0, y: 0 })
        return
      }

      const plugin = regionsPluginRef.current
      if (!plugin || !onRegionsChange) {
        return
      }

      const end = useRegionsForEditing ? Math.min(duration, time + 1) : time
      plugin.addRegion({
        start: time,
        end,
        color: "rgba(147, 51, 234, 0.5)",
        content: useRegionsForEditing
          ? `${(end - time).toFixed(2)}s`
          : undefined,
        drag: true,
        resize: useRegionsForEditing,
      })
      onRegionsChange(serializeAudioRegions(plugin.getRegions()))
      if (useRegionsForEditing) {
        requestAnimationFrame(() => requestAnimationFrame(styleResizeHandles))
      }
    }, [
      onRegionsChange,
      onWaveformDoubleClick,
      regionsPluginRef,
      wavesurfer,
      isReady,
      useRegionsForEditing,
      styleResizeHandles,
    ])

    useEffect(() => {
      onLoadError?.(loadError)
    }, [loadError, onLoadError])

    const selectedRegion = regionSelection.selectedRegionId
      ? regionsPluginRef.current
          ?.getRegions()
          .find(({ id }) => id === regionSelection.selectedRegionId)
      : undefined
    const selectedRegionBounds =
      selectedRegion && wavesurfer
        ? {
            start: selectedRegion.start,
            end: selectedRegion.end,
            duration: wavesurfer.getDuration(),
          }
        : null

    const speakerGutter = speakerAvatarParticipants ? SPEAKER_GUTTER_PIXELS : 0
    const laneGutterWidth =
      trackInsetPx && speakerAvatarParticipants
        ? Math.max(speakerGutter, trackInsetPx.leftPx - CONTROLS_EDGE_PIXELS)
        : speakerGutter

    return (
      <div
        ref={rootRef}
        className={`${
          compact
            ? "flex items-center gap-4"
            : "flex flex-col rounded-md bg-surface-default"
        } relative`}
      >
        <AddAudioMarkerButton
          visible={humanEvalMode && (isHovering || isButtonHovered)}
          position={buttonPosition}
          onAdd={handleAddMarkerAtPosition}
          onMouseEnter={handleButtonMouseEnter}
          onMouseLeave={handleButtonMouseLeave}
        />
        <AudioMarkerDialog
          canSubmit={Boolean(wavesurfer)}
          currentTime={wavesurfer?.getCurrentTime() ?? 0}
          hoverTime={hoverTime}
          isEditing={editingMarker !== null}
          label={newMarkerLabel}
          manualTime={manualTime}
          onClose={handleModalClose}
          onLabelChange={setNewMarkerLabel}
          onSubmit={editingMarker !== null ? handleUpdateMarker : addMarker}
          onTimeChange={setManualTime}
          open={showAddMarkerModal}
        />

        {compact && signedUrl && wavesurfer && (
          <CompactAudioControls
            isDownloading={isDownloading}
            isPlaying={isPlaying}
            onDownload={() => {
              void handleDownload()
            }}
            onPlayPause={onPlayPause}
            onPlaybackRateChange={handlePlaybackRateChange}
            playbackRate={playbackRate}
          />
        )}
        <div
          className={`${waveformVisible || contentHeight > 0 ? "flex" : "hidden"} w-full min-w-0 items-center ${
            compact ? "gap-3" : "px-4 pt-3"
          }`}
          style={
            !compact && trackInsetPx
              ? {
                  paddingLeft: Math.max(
                    0,
                    trackInsetPx.leftPx - laneGutterWidth,
                  ),
                  paddingRight:
                    trackInsetPx.rightPx - (trackInsetPx.tailPx ?? 0),
                }
              : undefined
          }
        >
          {speakerAvatarParticipants && (
            <SpeakerLaneAvatars
              laneHeight={laneHeight}
              participants={speakerAvatarParticipants}
              width={laneGutterWidth}
              contentHeight={contentHeight}
            />
          )}
          <div
            className={`overflow-hidden relative w-full flex-1 ${
              compact ? "h-[40px]" : "h-[60px] rounded-md"
            } ${
              compact || hasSpeakerLanes ? "" : "bg-coval-media-track"
            } pt-1 min-w-0`}
            style={{
              height: contentHeight > 0 ? waveformHeight : undefined,
              visibility: waveformVisible ? undefined : "hidden",
            }}
            data-audio-waveform-well
            onMouseMove={handleWaveformMouseMove}
            onMouseLeave={handleWaveformMouseLeave}
            onDoubleClick={
              onRegionsChange || onWaveformDoubleClick
                ? handleWaveformDoubleClick
                : undefined
            }
          >
            {onRegionsChange && (
              <AudioRegionSelectionMenu
                selectedRegionId={regionSelection.selectedRegionId}
                selectedPosition={regionSelection.selectedPosition}
                clampedPosition={regionSelection.clampedPosition}
                menuRef={regionSelection.menuRef}
                bounds={selectedRegionBounds}
                focusOnOpen={regionSelection.openedFromKeyboard}
                extra={regionMenuExtra}
                onBoundsChange={updateRegionBounds}
                onDelete={regionSelection.deleteSelectedRegion}
                onDismiss={regionSelection.dismissSelection}
              />
            )}

            {isReady && wavesurfer && wavesurfer.getDuration() > 0 && (
              <AudioSeekSlider
                currentTime={currentTime}
                duration={wavesurfer.getDuration()}
                getCurrentTime={() => wavesurfer.getCurrentTime()}
                onSeek={seekTo}
                describedBy={notice?.id}
              />
            )}
            <div className="absolute inset-0 flex w-full items-center">
              <div
                className="relative w-full"
                style={compact ? undefined : { height: waveformHeight }}
              >
                {hasSpeakerLanes && (
                  <SpeakerLaneBands
                    laneHeight={laneHeight}
                    contentHeight={contentHeight}
                  />
                )}
                <AudioWaveformContainer
                  ref={containerRef}
                  compact={compact}
                  duration={
                    isReady && waveformVisible
                      ? wavesurfer?.getDuration()
                      : undefined
                  }
                  tailPx={trackInsetPx?.tailPx ?? 0}
                />
              </div>
            </div>

            {!compact && <AudioTrackTail width={trackInsetPx?.tailPx ?? 0} />}

            <SpeakerChannelLayers
              childrenByChannel={channelWaveformOverlay}
              laneHeight={laneHeight}
              contentHeight={contentHeight}
            />
            {waveformOverlay}
            <AudioMarkerFlags
              markers={humanEvalMode ? markers : []}
              duration={wavesurfer?.getDuration() ?? 0}
              onEdit={handleEditMarker}
            />
            {(onRegionsChange || onWaveformDoubleClick) && (
              <Button
                variant="ghost"
                className="sr-only focus:not-sr-only focus:absolute focus:z-10"
                onClick={handleAddSegmentAtCurrentTime}
                aria-label="Add segment at current time"
              >
                Add segment at current time
              </Button>
            )}
          </div>
        </div>

        {contentHeight > 0 && (
          <SpeakerChannelLayers
            childrenByChannel={channelContent?.lanes}
            laneHeight={laneHeight}
            contentHeight={contentHeight}
            belowWaveform
          />
        )}

        {!compact && (!wavesurfer || loadError) && controlsExtra && (
          <div className="flex justify-end px-4">{controlsExtra}</div>
        )}
        {(isErrorAudio || loadError) && (
          <div className="flex justify-center items-center h-24">
            <p>Error loading audio</p>
          </div>
        )}
        {signedUrl && wavesurfer && !loadError && (
          <>
            {compact ? (
              <CompactAudioTime
                currentTime={currentTime}
                duration={wavesurfer.getDuration()}
              />
            ) : (
              <FullAudioControls
                extra={controlsExtra}
                leading={controlsLeading}
                {...controlsInsetPixels(trackInsetPx, speakerGutter)}
                elapsedSeconds={wavesurfer ? currentTime : null}
                totalSeconds={wavesurfer ? wavesurfer.getDuration() : null}
                isDownloading={isDownloading}
                isPlaying={isPlaying}
                onDownload={() => {
                  void handleDownload()
                }}
                onPlayPause={onPlayPause}
                onPlaybackRateChange={handlePlaybackRateChange}
                playbackRate={playbackRate}
                transcriptNotice={notice}
              />
            )}
          </>
        )}
      </div>
    )
  },
)
AudioPlayer.displayName = "AudioPlayer"
